import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ArrowLeft, Check, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { NoteWorkflowsCanvas } from "./NoteWorkflowsCanvas";
import { Button, Modal } from "../ui";
import { WorkflowBuilderApiError } from "../../features/workflowBuilder/response";
import type { NoteWorkflow, NoteWorkflowsDocument, WorkflowNodeSelection } from "./noteWorkflowsTypes";
import type { WorkflowLibraryView } from "./workflowLibraryTypes";
import styles from "./NoteWorkflows.module.css";

// Short display copy for the existing workflows; keep AI instructions in Description.
const WORKFLOW_SUMMARIES: Record<string, string> = {
    "journal entry": "Document daily care, observations and important events.",
    "goal reflection": "Reflect on care goals and review progress over time.",
    deviation: "Report incidents, investigate causes and follow up on actions.",
    "lex sarah": "Document and assess suspected misconduct in care services.",
    dokumentation: "Organize documentation and guide it through the relevant steps."
};

const DEFAULT_WORKFLOW_SUMMARY = "View and configure the steps in this workflow.";

function workflowSummary(title: string | undefined): string {
    const normalizedTitle = (title ?? "").replace(/[^\p{L}\p{N}\s]/gu, "").trim().toLocaleLowerCase();
    return WORKFLOW_SUMMARIES[normalizedTitle] ?? DEFAULT_WORKFLOW_SUMMARY;
}

interface WorkflowBrowserProps {
    title?: string;
    libraries?: readonly WorkflowLibraryView[];
    document: NoteWorkflowsDocument;
    loading: boolean;
    error: string | null;
    editing: boolean;
    saving: boolean;
    dirty: boolean;
    onEdit: (workflowId: string) => void;
    onCreate: () => string | null;
    toolbar: ReactNode;
    selectedNode: WorkflowNodeSelection | null;
    onCancel: () => void;
    onSave: () => void;
    onDelete: (workflow: NoteWorkflow, signal: AbortSignal) => Promise<void>;
    onReload: (signal: AbortSignal) => Promise<void>;
    deletionDisabled?: boolean;
    onNodeSelect: (selection: WorkflowNodeSelection) => void;
}

export function WorkflowBrowser({
    title,
    libraries = [],
    document,
    loading,
    error,
    editing,
    saving,
    dirty,
    onEdit,
    onCreate,
    onCancel,
    onSave,
    onDelete,
    onReload,
    deletionDisabled = false,
    onNodeSelect,
    toolbar,
    selectedNode
}: WorkflowBrowserProps) {
    const { t } = useTranslation();
    const [workflowId, setWorkflowId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [deleteTarget, setDeleteTarget] = useState<NoteWorkflow | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");
    const [deleteConflict, setDeleteConflict] = useState(false);
    const deletion = useRef<AbortController | null>(null);
    const deleteTrigger = useRef<HTMLButtonElement | null>(null);
    const list = useRef<HTMLDivElement>(null);
    useEffect(() => () => deletion.current?.abort(), []);
    const workflow = document.workflows.find(item => item.id === workflowId);
    const canvasDocument = useMemo<NoteWorkflowsDocument>(() => ({ ...document, workflows: workflow ? [workflow] : [] }), [document, workflow]);
    const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase();
    const visibleWorkflows = useMemo(
        () =>
            normalizedSearchQuery
                ? document.workflows.filter(item =>
                      [item.trigger.title ?? "", item.id, workflowSummary(item.trigger.title)]
                          .some(value => value.toLocaleLowerCase().includes(normalizedSearchQuery))
                  )
                : document.workflows,
        [document.workflows, normalizedSearchQuery]
    );
    const additionalCards = libraries.flatMap(library => library.cards);
    const visibleAdditionalCards = additionalCards.filter(card =>
        card.searchText.some(value => value.toLocaleLowerCase().includes(normalizedSearchQuery))
    );
    const totalCount = document.workflows.length + additionalCards.length;
    const visibleCount = visibleWorkflows.length + visibleAdditionalCards.length;
    const librariesLoading = loading || libraries.some(library => library.loading);
    const hasLoadError = Boolean(error) || libraries.some(library => library.hasError);
    const navigationBlocked = editing || saving || deleting || libraries.some(library => library.navigationBlocked);
    const workflowName = (title: string | undefined, id: string) =>
        title?.trim() || t("settingsPage.company.noteWorkflows.unnamedWorkflow", { defaultValue: "Untitled workflow · {{id}}", id });
    const createWorkflow = () => {
        const createdWorkflowId = onCreate();
        setSearchQuery("");
        if (createdWorkflowId !== null) setWorkflowId(createdWorkflowId);
    };
    const closeDelete = () => {
        if (deletion.current) return;
        setDeleteTarget(null);
        setDeleteError("");
        setDeleteConflict(false);
    };
    const confirmDelete = async () => {
        if (!deleteTarget || deletion.current || deletionDisabled || editing || saving) return;
        const controller = new AbortController();
        deletion.current = controller;
        setDeleting(true);
        setDeleteError("");
        try {
            // Conflicts require a fresh review; never automatically delete a changed workflow.
            if (deleteConflict) await onReload(controller.signal);
            else await onDelete(deleteTarget, controller.signal);
            if (!controller.signal.aborted) {
                setDeleteTarget(null);
                setDeleteConflict(false);
            }
        } catch (caught: unknown) {
            if (!controller.signal.aborted) {
                if (caught instanceof WorkflowBuilderApiError && caught.status === 409) {
                    setDeleteConflict(true);
                    setDeleteError(t("settingsPage.company.noteWorkflows.deleteConflict"));
                } else {
                    setDeleteError(caught instanceof Error ? caught.message : t(
                        deleteConflict ? "settingsPage.company.noteWorkflows.reloadError" : "settingsPage.company.noteWorkflows.deleteError"
                    ));
                }
            }
        } finally {
            if (!controller.signal.aborted) {
                deletion.current = null;
                setDeleting(false);
            }
        }
    };

    if (loading && libraries.length === 0)
        return (
            <p className={styles.emptyState} role="status">
                {t("settingsPage.loading")}
            </p>
        );
    if (error && document.workflows.length === 0 && libraries.length === 0)
        return (
            <p className={styles.errorText} role="alert">
                {error}
            </p>
        );
    if (workflow) {
        return (
            <div className={styles.workflowBrowserDetail}>
                <div className={styles.workflowBrowserToolbar}>
                    <button
                        type="button"
                        className={styles.workflowBrowserBack}
                        disabled={editing || saving}
                        onClick={() => {
                            setWorkflowId(null);
                        }}
                    >
                        <ArrowLeft size={16} />
                        {t("settingsPage.company.noteWorkflows.allWorkflows", { defaultValue: "All workflows" })}
                    </button>
                    <h3>{workflowName(workflow.trigger.title, workflow.id)}</h3>
                    <div className={styles.workflowBrowserActions}>
                        {editing ? (
                            <>
                                <button type="button" className={styles.cancelButton} disabled={saving} onClick={onCancel}>
                                    <X size={16} />
                                    {t("settingsPage.account.cancel")}
                                </button>
                                <button type="button" className={styles.saveButton} disabled={saving || !dirty} onClick={onSave}>
                                    <Check size={16} />
                                    {t(saving ? "settingsPage.account.saving" : "settingsPage.account.save")}
                                </button>
                            </>
                        ) : (
                            <button type="button" className={styles.editButton} disabled={navigationBlocked} onClick={() => onEdit(workflow.id)}>
                                <Pencil size={16} />
                                {t("settingsPage.account.edit")}
                            </button>
                        )}
                    </div>
                </div>
                {editing ? toolbar : null}
                {error ? (
                    <p className={styles.errorText} role="alert">
                        {error}
                    </p>
                ) : null}
                <div className={styles.workflowBrowserCanvas}>
                    <NoteWorkflowsCanvas
                        key={workflow.id}
                        document={canvasDocument}
                        selected={selectedNode}
                        onNodeSelect={selection => {
                            if (saving) return;
                            onNodeSelect(selection);
                        }}
                        variant="fullscreen"
                    />
                </div>
            </div>
        );
    }

    return (
        <div ref={list} tabIndex={-1} className={styles.workflowBrowserList}>
            <header className={styles.workflowBrowserHeader}>
                <div className={styles.workflowBrowserHeading}>
                    <h2>{title ?? t("settingsPage.company.noteWorkflows.title")}</h2>
                    <p>
                        {librariesLoading ? t("settingsPage.loading") : normalizedSearchQuery
                            ? t("settingsPage.company.noteWorkflows.searchResultCount", {
                                  defaultValue: "{{visible}} of {{total}} workflows",
                                  visible: visibleCount,
                                  total: totalCount
                              })
                            : t("settingsPage.company.noteWorkflows.workflowCount", { count: totalCount })}
                    </p>
                </div>
                <div className={styles.workflowBrowserControls}>
                    <label className={styles.workflowSearch}>
                        <Search size={17} aria-hidden="true" />
                        <span className={styles.editorWindowAccessibleTitle}>
                            {t("settingsPage.company.noteWorkflows.searchLabel", { defaultValue: "Search workflows" })}
                        </span>
                        <input
                            type="search"
                            value={searchQuery}
                            disabled={totalCount === 0}
                            placeholder={t("settingsPage.company.noteWorkflows.searchPlaceholder", {
                                defaultValue: "Search workflows"
                            })}
                            onChange={event => setSearchQuery(event.target.value)}
                        />
                    </label>
                    <button
                        type="button"
                        className={styles.workflowCreateButton}
                        disabled={navigationBlocked}
                        onClick={createWorkflow}
                    >
                        <Plus size={17} />
                        {t("settingsPage.company.noteWorkflows.actions.newWorkflow")}
                    </button>
                </div>
            </header>
            {error ? (
                <p className={styles.errorText} role="alert">
                    {error}
                </p>
            ) : null}
            {libraries.map((library, index) => <Fragment key={index}>{library.feedback}</Fragment>)}
            {totalCount === 0 && librariesLoading ? (
                <p className={styles.emptyState} role="status">{t("settingsPage.loading")}</p>
            ) : totalCount === 0 && hasLoadError ? null : totalCount === 0 ? (
                <div className={styles.workflowBrowserEmpty}>
                    <p>{t("settingsPage.company.noteWorkflows.empty")}</p>
                    <button type="button" className={styles.workflowCreateButton} disabled={navigationBlocked} onClick={createWorkflow}>
                        <Plus size={17} />
                        {t("settingsPage.company.noteWorkflows.actions.newWorkflow")}
                    </button>
                </div>
            ) : visibleCount === 0 ? (
                <div className={styles.workflowBrowserEmpty}>
                    <p>
                        {t("settingsPage.company.noteWorkflows.noSearchResults", {
                            defaultValue: "No workflows match your search."
                        })}
                    </p>
                    <button type="button" className={styles.workflowBrowserClearSearch} onClick={() => setSearchQuery("")}>
                        {t("settingsPage.company.noteWorkflows.clearSearch", { defaultValue: "Clear search" })}
                    </button>
                </div>
            ) : (
                <div className={styles.workflowBrowserCards}>
                    {visibleWorkflows.map(item => (
                        <div key={`document:${item.id}`} className={styles.workflowCard}>
                            <button
                                type="button"
                                className={styles.workflowNameCard}
                                disabled={navigationBlocked}
                                aria-label={workflowName(item.trigger.title, item.id)}
                                onClick={() => {
                                    setWorkflowId(item.id);
                                }}
                            >
                                <span className={styles.workflowCardName}>{workflowName(item.trigger.title, item.id)}</span>
                                <span className={styles.workflowCardSummary}>{workflowSummary(item.trigger.title)}</span>
                                <span className={styles.workflowCardCount}>
                                    {t("settingsPage.company.noteWorkflows.cardStepCount", {
                                        defaultValue: "{{count}} steps",
                                        count: item.steps.length + item.outcomes.length +
                                            (item.subflows ?? []).reduce((count, subflow) => count + 1 + subflow.steps.length, 0)
                                    })}
                                </span>
                            </button>
                            <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                iconShape="square"
                                className={styles.workflowDeleteButton}
                                disabled={navigationBlocked || deletionDisabled}
                                aria-label={t("settingsPage.company.noteWorkflows.deleteWorkflowLabel", {
                                    title: workflowName(item.trigger.title, item.id)
                                })}
                                title={t("settingsPage.company.noteWorkflows.deleteWorkflow")}
                                onClick={event => {
                                    deleteTrigger.current = event.currentTarget;
                                    setDeleteTarget(item);
                                    setDeleteError("");
                                    setDeleteConflict(false);
                                }}
                            >
                                <Trash2 size={18} aria-hidden="true" />
                            </Button>
                        </div>
                    ))}
                    {visibleAdditionalCards.map(card => <Fragment key={card.key}>{card.render(navigationBlocked)}</Fragment>)}
                </div>
            )}
            {deleteTarget && (
                <Modal
                    open
                    size="md"
                    className={styles.workflowDeleteDialog}
                    overlayClassName={styles.workflowDeleteOverlay}
                    onOpenChange={open => { if (!open) closeDelete(); }}
                    onCloseAutoFocus={event => {
                        event.preventDefault();
                        (deleteTrigger.current?.isConnected ? deleteTrigger.current : list.current)?.focus();
                    }}
                >
                    <Modal.Header
                        title={t("settingsPage.company.noteWorkflows.deleteConfirmTitle")}
                        description={t("settingsPage.company.noteWorkflows.deleteConfirmDescription", {
                            title: workflowName(deleteTarget.trigger.title, deleteTarget.id)
                        })}
                    />
                    {deleteError && <Modal.Body><p role="alert" className={styles.errorText}>{deleteError}</p></Modal.Body>}
                    <Modal.Footer className={styles.workflowDeleteActions}>
                        <Button autoFocus variant="secondary" disabled={deleting} onClick={closeDelete}>
                            {t("settingsPage.account.cancel")}
                        </Button>
                        <Button
                            variant={deleteConflict ? "secondary" : "danger"}
                            isLoading={deleting}
                            disabled={deleting || deletionDisabled}
                            onClick={() => void confirmDelete()}
                        >
                            {t(deleteConflict ? "settingsPage.company.noteWorkflows.reloadWorkflows" : "settingsPage.company.noteWorkflows.deleteWorkflow")}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}
